import type { FieldError, FieldErrors, FieldErrorsImpl, FieldValues, Merge } from 'react-hook-form';
import type { ErrorSummaryLabels, FocusRefs, ScrollRefs } from './FormContext';

import { isObject } from '@enonic/js-utils/value/isObject';
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import React from 'react';
import { useFormContext } from './FormContext';
import { Row, Stack } from '../Flex';
import { H2 } from '../heading/Heading';
import { Ul, Li } from '../List';
import { FontAwesomeIconWrapper } from '../../components/FontAwesomeIconWrapper';
import { Link } from '@hdir/ui/link';


interface ExtendedFieldError extends FieldError {
	focusEl?: HTMLElement;
	label?: string;
	scrollEl?: HTMLElement;
}


export function isFieldError(
	value: FieldError | Merge<FieldError, FieldErrorsImpl<FieldValues>> | undefined,
): value is FieldError {
	return (
		typeof value === 'object' &&
	  value !== null &&
	  typeof value.type === 'string' &&
	  (typeof value.message === 'string' || value.message === undefined) &&
	  (typeof value.ref === 'object' || value.ref === undefined)
	);
}

// The "aria-errormessage" attribute can only contain one element id,
// and the "id" global attribute must be unique in the whole document.
// So if an <ErrorMessage> is displayed twice, only one of them can be
// linked to the input field via aria-errormessage.
// Which means ErrorMessages in the ErrorSummary don't need id's.
const addErrorsFromObject = ({
	errors,
	errorMessages = [],
	errorSummaryLabels,
	focusRefs,
	path,
	scrollRefs,
}: {
	errors: FieldErrors<FieldValues>;
	errorMessages?: ExtendedFieldError[];
	errorSummaryLabels: ErrorSummaryLabels;
	focusRefs: FocusRefs;
	path?: string;
	scrollRefs: ScrollRefs;
}): ExtendedFieldError[] => {
	for (const key in errors) {
		const value = errors[key];
		const errorPath = path ? `${path}.${key}`: key;
		if (isFieldError(value)) {
			(value as ExtendedFieldError).label = errorSummaryLabels[errorPath];
			if (focusRefs[errorPath]) {
				(value as ExtendedFieldError).focusEl = focusRefs[errorPath];
			}
			if (scrollRefs[errorPath]) {
				(value as ExtendedFieldError).scrollEl = scrollRefs[errorPath];
			}
			errorMessages.push(value as ExtendedFieldError);
		} else if (isObject(value)) {
			addErrorsFromObject({
				errors: value as FieldErrors<FieldValues>,
				errorMessages,
				errorSummaryLabels,
				focusRefs,
				path: errorPath,
				scrollRefs,
			});
		} else {
			console.error(
				'ErrorSummary: Unsupported value type:', typeof value,
				' for key:', key ,
				' value:', value,
			);
		}
	}
	return errorMessages;
};


export const ErrorSummary = ({
	title,
}: {
	title: string;
}): React.JSX.Element | null => {
	const {
		errorSummaryLabels,
		focusRefs,
		formState: {
			errors,
		},
		scrollRefs,
	} = useFormContext();
	// console.debug(scrollRefs);

	if (!Object.keys(errors).length) return null;

	return (
		<Row
			bgColor='surfaceFeedbackErrorDefault'
			className='border border-feedback-error-default border-solid gap-md items-center rounded-sm'
			padding={{
				block: 'xl',
				inline: 'md',
			}}
		>
			<FontAwesomeIconWrapper
				className='color-border-feedback-error-default'
				icon={faCircleXmark}
			/>
			<Stack className='gap-md'>
				<H2>{title}</H2>
				<Ul className='ml-xl'>
					{
						addErrorsFromObject({
							errors,
							errorSummaryLabels,
							focusRefs,
							scrollRefs,
						}).map(
							({
								focusEl,
								scrollEl,
								label,
								// message,
								ref,
							}, index) => <Li
								key={`errorsummary-error-${index}`}
								marginTop={index ? 'md' : undefined}
							>
								<Link
									as='div'
									onClick={() => {
										// // @ts-expect-error TODO
										// console.debug('message', message, 'ref', ref, 'ref.current', ref.current);
										if (focusEl || scrollEl) {
											// console.debug('el', el);
											focusEl?.focus({ preventScroll: true });
											scrollEl?.scrollIntoView({ behavior: 'smooth' });
										} else if (ref?.focus) {
											// This only works if the ref is to an input field.
											ref.focus({
												// preventScroll: false // NOTE: Doesn't help scrolling to div's.
												// preventScroll: true, // Seems like react-hook-form's bulitin ref doesn't support the preventScroll option
											});
											// NOTE: ref.current is undefined!
											// ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
										}
									}}
								>
									{label}
									{/* {label ? <b>{label}:</b>: null}
									{message} */}
								</Link>
							</Li>,
						)
					}
				</Ul>
			</Stack>
		</Row>
	);
};
